import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import {
  INGREDIENT_OPTIONS,
  MPP_PRODUCT_ACTIONS,
  RECIPE_ACTIONS,
  getAllMPPProducts,
  getRecipeByIdFromState,
  selectBaseCalculations,
  selectRecipeImageSrc,
} from '@usf/ngrx-tools';
import { RecipeViewModelSelectors } from '../../recipes/selectors/recipe-view-model-selectors';
import {
  firstValueFrom,
  Observable,
  BehaviorSubject,
  of,
  map,
  tap,
  combineLatest,
  switchMap,
} from 'rxjs';
import {
  IngredientUnitsCalculations,
  MppFeatureFlag,
  recipeDetailViewModel,
} from '@app/mpp/model/recipe-detail-view-model';
import { TranslateService } from '@ngx-translate/core';
import { Button } from '@shared/models/ellipsis-option.model';
import { ActionSheetController, PopoverController } from '@ionic/angular';
import { EllipsisOptionsPopoverComponent } from '@shared/components/popovers/ellipsis-options/ellipsis-options-popover.component';
import { PlatformService } from '@panamax/app-state';
import { UOM, getUOMTranslation } from '@app/mpp/shared/enum/mpp-enums';
import { IngredientCard } from '@app/mpp/model/ingredient-card-model';
import { IngredientSearchResult } from '@app/mpp/shared/model/ingredient-search-result';
import { RecipeProductsTypeaheadService } from '@shared/services/typeahead/recipe-products-typeahead.service';
import { ProductService } from '@shared/services/product/product.service';
import { productWithAlternativesSelector } from '@shared/selectors/product.selectors';
import { ProductPriceUtil } from '@product-detail/utils/product-price.util';
import { selectedCustomer } from 'src/app/ngrx-customer/store/selectors/customer.selectors';
import { RecipeImageService } from '@app/mpp/shared/services/recipe-image/recipe-image.service';
import {
  IngredientList,
  ItemType,
  MppProduct,
  Recipe,
  UnitOfMeasure,
} from '@usf/mpp-types';
import { convertProductToMPP } from '../../recipes/helper/recipe-view-model-helper';
import { Product } from '@shared/models/product.model';
import { transformProductToIngredient } from '../helper/recipe-detail-helper';
import { IngredientCardClickEvent } from '@app/mpp/model/ingredient-card-click-event';
import { IngredientDeleteParams } from '@app/mpp/model/ingredient-card-click-event';
import {
  allNonUSFProducts,
  NON_USF_PRODUCTS,
  NonUSFProductActions,
} from '@usf/ngrx-product';
import { IngredientUomInfoPopoverComponent } from '@app/mpp/shared/components/details/ingredient-uom-info-popover/ingredient-uom-info-popover.component';
import { ZeroCostInfoPopoverComponent } from '@app/mpp/shared/components/zero-cost-info-popover/zero-cost-info-popover.component';
import { UserService } from '@app/user/services';

@Injectable({
  providedIn: 'root',
})
export class RecipeDetailService {
  private currentPopover: HTMLIonPopoverElement | null = null;
  private outsideClickHandler: (event: MouseEvent) => void;
  // used to slide open and close desktop splitView
  splitViewDistribution = new BehaviorSubject<number[]>([12, 0]);
  splitViewDistribution$: Observable<number[]> =
    this.splitViewDistribution.asObservable();

  private mppFeatureFlags$ = combineLatest([
    this.userService.showRecipeDetailTabs,
    this.userService.showNonUSFRecipeFeatures,
  ]).pipe(
    map(([showTab, showAddNonUsf]) => {
      return {
        showRecipeDetailUomTab: showTab,
        showRecipeDetailPrepTab: showTab,
        showAddNonUsfProductButton: showAddNonUsf,
      } as MppFeatureFlag;
    }),
  );

  private recipeDetailViewModel$ = combineLatest([
    this.store.select(this.recipeViewModelSelectors.recipeDetailViewModel$),
    this.splitViewDistribution$,
    this.mppFeatureFlags$,
  ]).pipe(
    map(([viewModel, values, mppFlags]) => {
      return {
        ...viewModel,
        leftHandVal: values[0],
        rightHandVal: values[1],
        mppFeatureflags: mppFlags,
      };
    }),
  );

  private INGREDIENT_UOM_VALUES = [
    UOM.EACH_PORTION,
    UOM.POUND,
    UOM.OUNCE,
    UOM.GRAM,
    UOM.GALLON,
    UOM.QUART,
    UOM.PINT,
    UOM.CUP,
    UOM.FLUID_OUNCE,
    UOM.TSP,
    UOM.TBSP,
  ];

  private groupModalIsOpenSubject: BehaviorSubject<boolean> =
    new BehaviorSubject(false);
  groupModalIsOpen$: Observable<boolean> =
    this.groupModalIsOpenSubject.asObservable();

  private editDetailsModalIsOpenSubject: BehaviorSubject<boolean> =
    new BehaviorSubject(false);
  editDetailsModalIsOpen$: Observable<boolean> =
    this.editDetailsModalIsOpenSubject.asObservable();

  private ingredientSearchModalIsOpenSubject: BehaviorSubject<boolean> =
    new BehaviorSubject(false);
  ingredientSearchModalIsOpen$: Observable<boolean> =
    this.ingredientSearchModalIsOpenSubject.asObservable();

  private deleteIngredientModalIsOpenSubject: BehaviorSubject<boolean> =
    new BehaviorSubject(false);
  deleteIngredientModalIsOpen$: Observable<boolean> =
    this.deleteIngredientModalIsOpenSubject.asObservable();

  private ingredientDetailModalIsOpenSubject: BehaviorSubject<boolean> =
    new BehaviorSubject(false);
  ingredientDetailModalIsOpen$: Observable<boolean> =
    this.ingredientDetailModalIsOpenSubject.asObservable();

  public currentDeleteParams: IngredientDeleteParams = {
    recipeId: '',
    ingredientIndex: -1,
  };

  private searchResultsForIngredient$ = this.store.select(
    this.recipeViewModelSelectors.getSearchResultsForIngredient$,
  );

  private searchTimeoutTracker = new BehaviorSubject<boolean>(false);
  searchTimeoutTracker$: Observable<boolean> =
    this.searchTimeoutTracker.asObservable();

  private typeAheadResults$ = this.typeAheadService.products$.pipe(
    tap(products => {
      this.productService.loadProducts(
        products.map(value => Number(value.ec_product_id)),
      );
    }),
    switchMap(products =>
      combineLatest([
        this.store.select(productWithAlternativesSelector),
        this.store.select(selectedCustomer),
        of(products),
      ]).pipe(
        map(([productMap, selectedCustomer, products]) => {
          return products.map(value => {
            const product = productMap.get(Number(value.ec_product_id));
            return transformProductToIngredient(
              product,
              selectedCustomer,
              value.ec_product_id,
              value.productName,
            );
          });
        }),
      ),
    ),
  );

  public get typeAheadResults(): Observable<IngredientSearchResult[]> {
    return this.typeAheadResults$;
  }

  public get searchResultsForIngredient(): Observable<
    IngredientSearchResult[]
  > {
    return this.searchResultsForIngredient$;
  }

  lastCardData: IngredientCard;

  private profileImageModalIsOpenSubject: BehaviorSubject<boolean> =
    new BehaviorSubject(false);
  profileImageModalIsOpen$: Observable<boolean> =
    this.profileImageModalIsOpenSubject.asObservable();

  //Timeout variables are so we can clearTimeout when needed.
  searchTimeout: any;
  splitTimeout: any;

  constructor(
    private store: Store,
    readonly recipeViewModelSelectors: RecipeViewModelSelectors,
    readonly translateService: TranslateService,
    readonly popoverController: PopoverController,
    readonly actionSheetController: ActionSheetController,
    readonly platformService: PlatformService,
    readonly typeAheadService: RecipeProductsTypeaheadService,
    readonly productService: ProductService,
    private recipeImageService: RecipeImageService,
    private userService: UserService,
  ) {
    this.outsideClickHandler = this.dismissIfOutside.bind(this);
  }

  public get recipeViewModel$(): Observable<recipeDetailViewModel> {
    return this.recipeDetailViewModel$;
  }

  public updateSelectedRecipe(recipeId: string) {
    this.store.dispatch(RECIPE_ACTIONS.selectRecipeForPageView({ recipeId }));
  }

  public updateShowHidePortion(show: boolean) {
    this.store.dispatch(RECIPE_ACTIONS.showHidePortion({ show }));
  }

  public showServingUOMDropdown(event: Event, recipeID: string) {
    const buttons = this.getUOMButtons(this.updateServingUOM, recipeID);
    this.platformService.isTouch.value
      ? this.openSelectionsTouch(buttons)
      : this.openSelectionsDesktop(event, buttons);
  }
  public async showGroupNameDropdown(event: Event, recipeID: string) {
    const groups = await firstValueFrom(
      this.store.select(this.recipeViewModelSelectors.getGroupsForRecipes$),
    );
    const buttons = this.getGroupButtons(groups, recipeID);
    this.platformService.isTouch.value
      ? this.openSelectionsTouch(buttons)
      : this.openSelectionsDesktop(event, buttons);
  }

  public showYieldUOMDropdown(event: Event, recipeID: string) {
    const buttons = this.getUOMButtons(this.updateYieldUOM, recipeID);
    this.platformService.isTouch.value
      ? this.openSelectionsTouch(buttons)
      : this.openSelectionsDesktop(event, buttons);
  }

  public updateYield(value: number, recipeID: string) {
    const num = new Number(value).valueOf();
    this.store.dispatch(
      RECIPE_ACTIONS.updateYieldValue({ recipeId: recipeID, value: num }),
    );
  }

  public updateServing(value: number, recipeID: string) {
    const num = new Number(value).valueOf();
    this.store.dispatch(
      RECIPE_ACTIONS.updateServingValue({ recipeId: recipeID, value: num }),
    );
  }

  public updateServingPerRecipe(value: number, recipeID: string) {
    const num = new Number(value).valueOf();
    this.store.dispatch(
      RECIPE_ACTIONS.updateServingPerRecipeValue({
        recipeId: recipeID,
        value: num,
      }),
    );
  }

  public updateServingUOM = (value: string, recipeID: string) => {
    this.store.dispatch(
      RECIPE_ACTIONS.updateServingUOM({ recipeId: recipeID, value }),
    );
    if (this.platformService.isTouch.value) {
      this.actionSheetController.dismiss();
    } else {
      this.popoverController.dismiss();
    }
  };

  public updateYieldUOM = (value: string, recipeID: string) => {
    this.store.dispatch(
      RECIPE_ACTIONS.updateYieldUOM({ recipeId: recipeID, value }),
    );
    if (this.platformService.isTouch.value) {
      this.actionSheetController.dismiss();
    } else {
      this.popoverController.dismiss();
    }
  };

  public showUOMInfoPopover = async (event: Event) => {
    const popover = await this.popoverController.create({
      component: IngredientUomInfoPopoverComponent,
      showBackdrop: true,
      cssClass: 'uom-info-popover',
      event,
      mode: 'ios',
      alignment: 'center',
    });
    await popover.present();
  };

  public showCostInfoPopover = async (event: Event) => {
    if (this.currentPopover) {
      await this.currentPopover.dismiss();
      return;
    }

    const popover = await this.popoverController.create({
      component: ZeroCostInfoPopoverComponent,
      event: event,
      mode: 'ios',
      backdropDismiss: false,
      translucent: true,
      showBackdrop: true,
      cssClass: 'zero-cost-info-popover',
    });

    this.currentPopover = popover;

    popover.onDidDismiss().then(() => {
      window.removeEventListener('click', this.outsideClickHandler, true);
      this.currentPopover = null;
    });

    await popover.present();
    window.addEventListener('click', this.outsideClickHandler, true);
  };

  private dismissIfOutside(event: MouseEvent) {
    if (!this.currentPopover) {
      return;
    }

    const path = event.composedPath();
    if (!path.includes(this.currentPopover)) {
      this.currentPopover.dismiss();
    }
  }

  public dismissCostInfoIconPopover() {
    this.popoverController.dismiss();
  }

  public updateIngredientUOM = (
    value: string,
    recipeID: string,
    ingredientId: number,
  ) => {
    this.store.dispatch(
      RECIPE_ACTIONS.updateIngredientUOM({
        recipeId: recipeID,
        value,
        ingredientId,
      }),
    );
    if (this.platformService.isTouch.value) {
      this.actionSheetController.dismiss();
    } else {
      this.popoverController.dismiss();
    }
  };

  public updateIngredientQuantity = (
    value: string,
    recipeID: string,
    ingredientId: number,
  ) => {
    const num = new Number(value).valueOf();
    this.store.dispatch(
      RECIPE_ACTIONS.updateIngredientQuantity({
        recipeId: recipeID,
        value: num,
        ingredientId,
      }),
    );
  };

  public updateIngredientNote = (
    value: string,
    recipeID: string,
    ingredientId: number,
  ) => {
    this.store.dispatch(
      RECIPE_ACTIONS.updateIngredientNote({
        recipeId: recipeID,
        value,
        ingredientId,
      }),
    );
  };

  public updateGroupName = (value: string, recipeID: string) => {
    this.store.dispatch(
      RECIPE_ACTIONS.updateGroupName({ recipeId: recipeID, groupName: value }),
    );
    if (this.platformService.isTouch.value) {
      this.actionSheetController.dismiss();
    } else {
      this.popoverController.dismiss();
    }
  };

  public updateRecipeName = (value: string, recipeID: string) => {
    this.store.dispatch(
      RECIPE_ACTIONS.updateRecipeName({ recipeId: recipeID, name: value }),
    );
  };

  public updateDescription = (value: string, recipeID: string) => {
    this.store.dispatch(
      RECIPE_ACTIONS.updateDescription({
        recipeId: recipeID,
        description: value,
      }),
    );
  };

  public updateRecipeLockStatus = (lockInd: boolean, recipeID: string) => {
    this.store.dispatch(
      RECIPE_ACTIONS.updateRecipeLockInd({
        recipeId: recipeID,
        value: lockInd,
      }),
    );
  };

  async openSelectionsDesktop(event: Event, buttons: Button[]) {
    const popover = await this.popoverController.create({
      component: EllipsisOptionsPopoverComponent,
      componentProps: {
        buttons,
      },
      showBackdrop: false,
      cssClass: 'ellipsis-options-popover-desktop',
      event,
      mode: 'ios',
      alignment: 'center',
    });
    await popover.present();
  }

  async openSelectionsTouch(buttons: Button[]) {
    buttons.push({
      text: this.translateService.instant('i18n.common.cancel'),
      role: 'cancel',
      cssClass: 'cancel-button',
    });
    const actionSheet = await this.actionSheetController.create({
      cssClass: 'action-sheet-touch',
      mode: 'ios',
      buttons,
    });
    await actionSheet.present();
  }

  public openGroupNameModal() {
    this.groupModalIsOpenSubject.next(true);
  }

  public handleGroupNameModalDismiss() {
    this.groupModalIsOpenSubject.next(false);
  }

  public handleCustomGroupSubmit(groupName: string, recipeID: string) {
    this.updateGroupName(groupName, recipeID);
    this.groupModalIsOpenSubject.next(false);
  }

  public getGroupButtons(groups: string[], recipeID: string) {
    const buttons = groups.map(group => {
      return {
        text: group,
        handler: () => this.updateGroupName(group, recipeID),
      };
    });

    buttons.push({
      text: 'Add Custom',
      handler: () => this.openGroupNameModal(),
    });

    return buttons;
  }

  public getUOMButtons(callback: Function, recipeID: string) {
    return [
      {
        text: this.translateService.instant(getUOMTranslation(UOM.EACH)),
        handler: () => callback(UOM.EACH, recipeID),
      },
      {
        text: this.translateService.instant(getUOMTranslation(UOM.POUND)),
        handler: () => callback(UOM.POUND, recipeID),
      },
      {
        text: this.translateService.instant(getUOMTranslation(UOM.OUNCE)),
        handler: () => callback(UOM.OUNCE, recipeID),
      },
      {
        text: this.translateService.instant(getUOMTranslation(UOM.GRAM)),
        handler: () => callback(UOM.GRAM, recipeID),
      },
      {
        text: this.translateService.instant(getUOMTranslation(UOM.GALLON)),
        handler: () => callback(UOM.GALLON, recipeID),
      },
      {
        text: this.translateService.instant(getUOMTranslation(UOM.QUART)),
        handler: () => callback(UOM.QUART, recipeID),
      },
      {
        text: this.translateService.instant(getUOMTranslation(UOM.PINT)),
        handler: () => callback(UOM.PINT, recipeID),
      },
      {
        text: this.translateService.instant(getUOMTranslation(UOM.CUP)),
        handler: () => callback(UOM.CUP, recipeID),
      },
      {
        text: this.translateService.instant(getUOMTranslation(UOM.FLUID_OUNCE)),
        handler: () => callback(UOM.FLUID_OUNCE, recipeID),
      },
    ];
  }

  public getIngredientUOMButtons(
    callback: (value: string, recipeId: string, seqNum: number) => void,
    recipeID: string,
    ingredientSeqNumber: number,
  ) {
    return this.INGREDIENT_UOM_VALUES.map(uom => ({
      text: this.translateService.instant(getUOMTranslation(uom)),
      handler: () => callback(uom, recipeID, ingredientSeqNumber),
    }));
  }

  public getMobileActionsButtons(
    recipeID: string,
    lockEdits: boolean,
  ): Button[] {
    const buttons: Button[] = [];

    if (
      this.platformService.platformType ===
      this.platformService.platformEnum.mobile
    ) {
      buttons.push({
        text: this.translateService.instant(
          'i18n.menuProfitPro.actions.editDetails',
        ),
        cssClass: lockEdits ? 'disabled-button' : '',
        handler: lockEdits ? undefined : () => this.openEditDetailsModal(),
      });
    }

    return buttons;
  }

  public openIngredientDetailModal(): void {
    this.ingredientDetailModalIsOpenSubject.next(true);
  }

  public dismissIngredientDetailModal(): void {
    this.ingredientDetailModalIsOpenSubject.next(false);
  }

  public openEditDetailsModal(): void {
    this.editDetailsModalIsOpenSubject.next(true);
  }

  public dismissEditDetailsModal(): void {
    this.editDetailsModalIsOpenSubject.next(false);
  }

  public handleEditDetailsSubmission(
    updates: Map<string, string>,
    recipeID: string,
  ): void {
    if (updates.has('description')) {
      this.updateDescription(updates.get('description') || '', recipeID);
    }
    if (updates.has('name')) {
      this.updateRecipeName(updates.get('name') || '', recipeID);
    }
    if (updates.has('group')) {
      this.updateGroupName(updates.get('group') || '', recipeID);
    }
    this.dismissEditDetailsModal();
  }

  public openProfileImageModal(): void {
    this.profileImageModalIsOpenSubject.next(true);
  }

  public dismissProfileImageModal(): void {
    this.profileImageModalIsOpenSubject.next(false);
  }

  public handleProfileImageModalOnSave(
    updates: Map<string, string>,
    recipeID: string,
  ): void {
    const fileName = updates.get('fileName') ?? '';
    const imageType = updates.get('extension') ?? '';
    const base64Value = updates.get('value') ?? '';

    this.recipeImageService.uploadRecipeImage(recipeID, {
      fileName,
      type: imageType,
      value: base64Value,
    });

    setTimeout(() => {
      this.dismissProfileImageModal();
    }, 300);
  }

  public async showIngredientUOMDropdown(
    event: Event,
    recipeID: string,
    ingredientSeqNumber: number,
  ) {
    const buttons = this.getIngredientUOMButtons(
      this.updateIngredientUOM,
      recipeID,
      ingredientSeqNumber,
    );
    if (this.platformService.isTouch.value) {
      await this.openSelectionsTouch(buttons);
    } else {
      await this.openSelectionsDesktop(event, buttons);
    }
  }

  public async showMobileActions(
    recipeID: string,
    lockEdits: boolean,
  ): Promise<void> {
    const buttons = this.getMobileActionsButtons(recipeID, lockEdits);

    if (buttons.length === 0) {
      return;
    }

    buttons.push({
      text: this.translateService.instant('i18n.common.cancel'),
      role: 'cancel',
      cssClass: 'cancel-button',
    });
    const actionSheet = await this.actionSheetController.create({
      cssClass: 'action-sheet-touch',
      mode: 'ios',
      buttons,
    });
    await actionSheet.present();
  }

  updateMppProductReference = (mppProduct: MppProduct, recipe: Recipe) => {
    if (!(mppProduct.referencedRecipeList ?? []).includes(recipe.recipeId))
      mppProduct.referencedRecipeList = (
        mppProduct.referencedRecipeList ?? []
      ).concat(recipe.recipeId);
    mppProduct.updateDtm = new Date();
  };

  public async addProductsToRecipeFromSearch(products: Product[]) {
    const recipe = await firstValueFrom(this.recipeViewModel$);
    const customerInfo = await firstValueFrom(
      this.store.select(selectedCustomer),
    );
    const mppProductState = await firstValueFrom(
      this.store.select(getAllMPPProducts()),
    );

    const prodNumbers = products.map(value => value.productNumber);

    let mppProducts = mppProductState?.filter(value =>
      prodNumbers.includes(value.productNumber),
    );

    let missingProductNumbers = prodNumbers.filter(prodNum => {
      return !mppProducts.find(mpp => mpp.productNumber === prodNum);
    });

    if (!!missingProductNumbers && missingProductNumbers.length > 0) {
      mppProducts.push(
        ...missingProductNumbers.map(missingNumber => {
          return convertProductToMPP(
            products.find(product => product.productNumber === missingNumber),
            customerInfo.customerNumber,
            customerInfo.divisionNumber,
          );
        }),
      );
    }

    mppProducts.forEach(mppProduct => {
      this.updateMppProductReference(mppProduct, recipe);
    });

    if (mppProducts.length > 0) {
      this.store.dispatch(
        RECIPE_ACTIONS.addIngredientToRecipeProductMultiple({
          recipeId: recipe.recipeId,
          ingredients: mppProducts.map(mppProduct => {
            return {
              itemNumber: mppProduct.productNumber.toString(),
              type: ItemType.USFPRODUCT,
            };
          }) as IngredientList[],
          mppProducts,
        }),
      );
    }
  }

  public async addIngredient(
    searchResult: IngredientSearchResult,
  ): Promise<void> {
    const recipe = await firstValueFrom(this.recipeViewModel$);
    const ingredient = {
      itemNumber: searchResult.id,
      type:
        searchResult.types === INGREDIENT_OPTIONS.PRODUCT
          ? ItemType.USFPRODUCT
          : ItemType.RECIPE,
    } as IngredientList;
    switch (searchResult.types) {
      case INGREDIENT_OPTIONS.PRODUCT:
        const customerInfo = await firstValueFrom(
          this.store.select(selectedCustomer),
        );
        const mppProductState = await firstValueFrom(
          this.store.select(getAllMPPProducts()),
        );
        let mppProduct = mppProductState?.find(
          value =>
            value.productNumber ===
            searchResult.product?.summary?.productNumber,
        );
        if (!mppProduct) {
          mppProduct = convertProductToMPP(
            searchResult.product,
            customerInfo.customerNumber,
            customerInfo.divisionNumber,
          );
        }
        this.updateMppProductReference(mppProduct, recipe);
        this.store.dispatch(
          RECIPE_ACTIONS.addIngredientToRecipeProduct({
            recipeId: recipe.recipeId,
            ingredient,
            mppProduct,
          }),
        );

        if (!!searchResult.typeAheadData)
          this.typeAheadService.selectInstantProduct(
            searchResult.typeAheadData,
          );

        break;
      case INGREDIENT_OPTIONS.RECIPE:
        this.store.dispatch(
          RECIPE_ACTIONS.addIngredientToRecipeRecipe({
            recipeId: recipe.recipeId,
            ingredient,
          }),
        );
        break;
    }
  }

  public openIngredientSearchModal(): void {
    const typeLimit =
      this.platformService.platformEnum.desktop ===
      this.platformService.platformType
        ? 5
        : 3;
    this.typeAheadService.initializeRecipeProductsTypeAhead(typeLimit);
    this.ingredientSearchModalIsOpenSubject.next(true);
    this.store.dispatch(
      RECIPE_ACTIONS.updateIngredientSearchKey({ searchKey: '' }),
    );
    this.typeAheadService.handleInputChange('');
  }

  public closeIngredientSearchModal(): void {
    this.ingredientSearchModalIsOpenSubject.next(false);
    this.typeAheadService.disableTypeahead();
  }

  public handleNewIngredientSearchRequest(value: string) {
    this.searchTimeoutTracker.next(true);
    // wanting to avoid the searching showing no results while typing
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
    this.searchTimeout = setTimeout(() => {
      this.searchTimeoutTracker.next(false);
    }, 750);
    this.typeAheadService.handleInputChange(value);
    this.store.dispatch(
      RECIPE_ACTIONS.updateIngredientSearchKey({ searchKey: value }),
    );
  }

  public deleteIngredientFromRecipe = async (
    recipeId: string,
    ingredientIndex: number,
  ) => {
    const recipe = await firstValueFrom(this.recipeViewModel$);
    const ingredient = recipe.ingredientList[ingredientIndex];
    let count = 0;
    recipe.ingredientList?.forEach(item => {
      if (
        item.type === ingredient.type &&
        item.itemNumber === ingredient.itemNumber
      ) {
        count++;
      }
    });

    if (count === 1 && ingredient.type === ItemType.USFPRODUCT) {
      const shouldDelete = await this.determineIfProductShouldDeleteOrUpdate(
        recipe,
        ingredient,
      );
      if (shouldDelete) {
        this.store.dispatch(
          MPP_PRODUCT_ACTIONS.deleteMppProduct({
            productNumber: Number(ingredient.itemNumber),
          }),
        );
      } else {
        const mppProductState = await firstValueFrom(
          this.store.select(getAllMPPProducts()),
        );
        const mppProduct = mppProductState.find(
          item => item.productNumber === Number(ingredient.itemNumber),
        );
        mppProduct.referencedRecipeList = (
          mppProduct?.referencedRecipeList ?? []
        ).filter(item => item !== recipeId);
        this.store.dispatch(
          MPP_PRODUCT_ACTIONS.updateMppProduct({ mppProduct }),
        );
      }
    }

    if (count === 1 && ingredient.type === ItemType.NONUSFPRODUCT) {
      const nonUSFState = await firstValueFrom(
        this.store.select(allNonUSFProducts()),
      );
      const nonUSFProduct = nonUSFState.find(
        item => item.productNumber === Number(ingredient.itemNumber),
      );
      let recipeReferences = nonUSFProduct?.referencedRecipeList;
      if (!!recipeReferences) {
        recipeReferences = recipeReferences.filter(item => item !== recipeId);
      }
      this.store.dispatch(
        NonUSFProductActions.updateReferences({
          productNumbers: [Number(ingredient.itemNumber)],
          recipeReferences,
        }),
      );
    }

    this.store.dispatch(
      RECIPE_ACTIONS.deleteIngredient({
        recipeId,
        index: ingredientIndex,
        itemType: ingredient.type,
        childRecipeID:
          ingredient.type === ItemType.RECIPE
            ? ingredient.itemNumber
            : undefined,
      }),
    );

    this.dismissDeleteIngredientModal();
  };

  public openDeleteIngredientModal(deleteParams: IngredientDeleteParams): void {
    this.currentDeleteParams = deleteParams;
    this.deleteIngredientModalIsOpenSubject.next(true);
  }

  public dismissDeleteIngredientModal(): void {
    this.deleteIngredientModalIsOpenSubject.next(false);
  }

  public updateMppProductMeasurements = async (
    unitOfMeasure: UnitOfMeasure,
    prodNumber: number,
  ) => {
    const productState = await firstValueFrom(
      this.store.select(getAllMPPProducts()),
    );
    const mppProduct = productState.find(
      product => product.productNumber === prodNumber,
    );
    if (!!mppProduct) {
      this.store.dispatch(
        MPP_PRODUCT_ACTIONS.updateMppProduct({
          mppProduct: { ...mppProduct, unitOfMeasure },
        }),
      );
      this.store.dispatch(
        RECIPE_ACTIONS.updateCostIngredient({
          costUpdateDto: {
            costUpdateItem: [
              {
                id: mppProduct.productNumber.toString(),
                cost: mppProduct.casePrice,
                productYield: mppProduct.productYield ?? 1,
                unitOfMeasure: unitOfMeasure,
                referencedRecipeList: mppProduct.referencedRecipeList ?? [],
                referencedMenuItemList: mppProduct.referencedMenuItemList ?? [],
                referencedEventPlanList:
                  mppProduct.referencedEventPlanList ?? [],
              },
            ],
          },
        }),
      );
    }
  };

  public handleDeleteIngredientSubmit(
    recipeId: string,
    ingredientIndex: number,
  ): void {
    this.deleteIngredientFromRecipe(recipeId, ingredientIndex);
  }

  public handleDeleteIngredientSubmitFromState(): void {
    this.deleteIngredientFromRecipe(
      this.currentDeleteParams.recipeId,
      this.currentDeleteParams.ingredientIndex,
    );
  }

  public async determineIfProductShouldDeleteOrUpdate(
    recipe: recipeDetailViewModel,
    ingredient: IngredientList,
  ) {
    let count = 0;
    recipe.ingredientList?.forEach(item => {
      if (
        item.type === ingredient.type &&
        item.itemNumber === ingredient.itemNumber
      ) {
        count++;
      }
    });

    if (count === 1) {
      const mppProductState = await firstValueFrom(
        this.store.select(getAllMPPProducts()),
      );
      const mppProduct = mppProductState.find(
        item => item.productNumber === Number(ingredient.itemNumber),
      );
      if (!!mppProduct) {
        if (
          mppProduct?.referencedMenuItemList?.length > 0 ||
          mppProduct?.referencedEventPlanList?.length > 0 ||
          mppProduct?.referencedRecipeList.length > 1
        ) {
          return false;
        } else {
          return true;
        }
      } else {
        return false;
      }
    } else {
      return false;
    }
  }

  public getIngredientCardButtons(recipeID: string, ingredientIndex: number) {
    const dismiss = () => {
      if (this.platformService.isTouch.value) {
        this.actionSheetController.dismiss();
      } else {
        this.popoverController.dismiss();
      }
    };
    return [
      {
        text: this.translateService.instant('i18n.common.delete'),
        handler: async () => {
          const recipe = await firstValueFrom(this.recipeViewModel$);
          const ingredient = recipe.ingredientList[ingredientIndex];
          if (ingredient.type === ItemType.USFPRODUCT) {
            const shouldOpenModal =
              await this.determineIfProductShouldDeleteOrUpdate(
                recipe,
                ingredient,
              );
            if (shouldOpenModal) {
              this.openDeleteIngredientModal({
                recipeId: recipeID,
                ingredientIndex,
              });
            } else {
              this.deleteIngredientFromRecipe(recipeID, ingredientIndex);
            }
          } else {
            this.deleteIngredientFromRecipe(recipeID, ingredientIndex);
          }

          dismiss();
        },
      },
    ];
  }

  public handleIngredientEllipsisClick(
    $event: IngredientCardClickEvent,
    recipeId: string,
  ) {
    const buttons = this.getIngredientCardButtons(
      recipeId,
      $event.ingredientCard.index,
    );
    this.platformService.isTouch.value
      ? this.openSelectionsTouch(buttons)
      : this.openSelectionsDesktop($event.event, buttons);
  }

  public updateLastSelected(lastSelected: number) {
    this.store.dispatch(RECIPE_ACTIONS.selectIngredient({ id: lastSelected }));
  }

  public updateIngredientBaseCalculations(
    calculations: IngredientUnitsCalculations,
  ) {
    this.store.dispatch(RECIPE_ACTIONS.updateCalculations({ calculations }));
  }

  public resetSplitView = () => {
    this.splitViewDistribution.next([12, 0]);
  };

  public async getCurrentCalculations() {
    return firstValueFrom(this.store.select(selectBaseCalculations()));
  }

  // split View is a desktop only feature.
  public moveIntoSplitView = () => {
    if (this.splitTimeout) {
      clearTimeout(this.splitTimeout);
    }
    if (this.splitViewDistribution.value[0] > 6) {
      const timeOutFunc = () => {
        if (this.splitViewDistribution.value[0] > 6) {
          let val1 = this.splitViewDistribution.value[0] - 0.25;
          let val2 = this.splitViewDistribution.value[1] + 0.25;
          this.splitViewDistribution.next([val1, val2]);
          this.splitTimeout = setTimeout(timeOutFunc, 25);
        }
      };
      this.splitTimeout = setTimeout(timeOutFunc, 25);
    }
  };

  public moveBackToSingleView = () => {
    if (this.splitTimeout) {
      clearTimeout(this.splitTimeout);
    }
    if (this.splitViewDistribution.value[0] < 12) {
      const timeOutFunc = () => {
        if (this.splitViewDistribution.value[0] < 12) {
          let val1 = this.splitViewDistribution.value[0] + 0.25;
          let val2 = this.splitViewDistribution.value[1] - 0.25;
          this.splitViewDistribution.next([val1, val2]);
          this.splitTimeout = setTimeout(timeOutFunc, 25);
        }
      };
      this.splitTimeout = setTimeout(timeOutFunc, 25);
    }
  };
}
